import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, orgStore, tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(null);
      setOrg(null);
      setOrgs([]);
      setLoading(false);
      return;
    }
    try {
      const [meRes, orgRes, listRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/orgs/current"),
        api.get("/orgs/mine"),
      ]);
      setUser(meRes.data);
      setOrg(orgRes.data);
      setOrgs(listRes.data || []);
      if (orgRes.data?.id) orgStore.set(orgRes.data.id);
    } catch (e) {
      tokenStore.clear();
      orgStore.clear();
      setUser(null);
      setOrg(null);
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const completeAuth = useCallback((payload) => {
    // payload: {token, user, org}
    tokenStore.set(payload.token);
    if (payload.org?.id) orgStore.set(payload.org.id);
    setUser(payload.user);
    setOrg(payload.org);
    // best-effort refresh of orgs list
    api.get("/orgs/mine").then((r) => setOrgs(r.data || [])).catch(() => {});
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    completeAuth(res.data);
    return res.data;
  }, [completeAuth]);

  const signup = useCallback(async (payload) => {
    const res = await api.post("/auth/signup", payload);
    completeAuth(res.data);
    return res.data;
  }, [completeAuth]);

  const loginWithGoogleSession = useCallback(async (session_id) => {
    const res = await api.post("/auth/google", { session_id });
    completeAuth(res.data);
    return res.data;
  }, [completeAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    tokenStore.clear();
    orgStore.clear();
    setUser(null);
    setOrg(null);
    setOrgs([]);
  }, []);

  const switchOrg = useCallback(async (orgId) => {
    const res = await api.post("/orgs/switch", { org_id: orgId });
    tokenStore.set(res.data.token);
    orgStore.set(res.data.id);
    await refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{
        user, org, orgs, loading,
        login, signup, logout, refresh,
        loginWithGoogleSession, switchOrg, setOrg, setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
